import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import { AppThunk } from "../store/configureStore";
import StatisticService, {STATS_ADV_QUERY, STATS_QUERY} from "Services/statistic";
import Dayjs from "dayjs";
import { getTimeZone } from "../Services/time";
import ActiveUsersService from "Services/activeUsers";
import { ISelectFilters, ITotalStat } from "../types/statistic";
import UserService from "../Services/user";

interface IRequest<T> {
  isFetching: boolean,
  data: T,
  error: Error | unknown,
}

interface IStatState {
  loading: boolean;
  loadingTotal: boolean;
  dataStat: [];
  total: ITotalStat;
  selectFilters: ISelectFilters;
  updatedStats: {};
  errors: any;
  activeSubscribers: IRequest<number>;
}

const today = Dayjs().format("YYYY-MM-DD");
const las14days = Dayjs().add(-14, "days").format("YYYY-MM-DD");

export const EMPTY_STAT_VALUE = "...";

const initialFilters = {
  [STATS_QUERY.GROUP]: "day",
  [STATS_QUERY.DAY_FROM]: las14days,
  [STATS_QUERY.DAY_TO]: today,
  [STATS_QUERY.OS]: [],
  [STATS_ADV_QUERY.COUNTRIES]: [],
  [STATS_ADV_QUERY.OSES]: [],
  [STATS_ADV_QUERY.COUNTRY_ID]: [],
  [STATS_ADV_QUERY.OS_ID]: [],
  [STATS_QUERY.ZONE]: [],
  [STATS_QUERY.REQUEST_VAR]: [],
  [STATS_QUERY.TIME_ZONE]: getTimeZone(),
};

const initialState: IStatState = {
  loading: false,
  loadingTotal: false,
  dataStat: [],
  total: {
    clicks: EMPTY_STAT_VALUE,
    impressions: EMPTY_STAT_VALUE,
    ctr: EMPTY_STAT_VALUE,
  },
  selectFilters: initialFilters,
  updatedStats: {},
  errors: {},
  activeSubscribers: {
    isFetching: false,
    data: 0,
    error: null,
  },
};

const statisticSlice = createSlice({
  name: "stat",
  initialState,
  reducers: {
    setActiveSubscribers: (state, action: PayloadAction<IRequest<number>>) => {
      const { payload } = action;
      state.activeSubscribers = payload;
    },
    setUpdatedStat: (state, action) => {
      const { payload } = action;
      state.updatedStats = payload;
    },
    resetFilters: (state) => {
      state.selectFilters = initialFilters;
    },
    setQuery: (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.selectFilters = {
        ...state.selectFilters,
        ...payload,
      };
    },
    setLoader: (state) => {
      state.loading = true;
      state.errors = initialState.errors;
    },
    setTotalLoader: (state) => {
      state.loadingTotal = true;
      state.errors = initialState.errors;
    },
    setStat: (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.dataStat = payload.result[0]?.values || [];
    },
    setTotalStat: (state, action) => {
      const { payload } = action;
      if (payload.status === "failed") {
        state.total = initialState.total;
      } else {
        state.total = payload;
      }
      state.loadingTotal = false;
    },
    setInitialStat: (state) => {
      state.dataStat = initialState.dataStat;
    },
    setInitialTotal: (state) => {
      state.total = initialState.total;
    },
    setError: (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.errors = payload;
    },
  },
});

export const {
  setLoader,
  setStat,
  setError,
  setQuery,
  resetFilters,
  setInitialStat,
  setInitialTotal,
  setTotalStat,
  setTotalLoader,
  setUpdatedStat,
  setActiveSubscribers
} = statisticSlice.actions;

export const loadStat = (
  id: number,
  type: string,
  period: number,
  step: number
): AppThunk => async (dispatch) => {
  dispatch(setInitialStat());
  dispatch(setLoader());

  try {
    const graph = await StatisticService.loadPrometheus(id, type, period, step);
    dispatch(setStat(graph.data));
  } catch (error) {
    dispatch(setError(error));
  }
};

export const loadTotalStat = (id: number): AppThunk => async (dispatch) => {
  dispatch(setInitialTotal());
  dispatch(setTotalLoader());

  try {
    const total = await StatisticService.loadTotalPrometheus(id);
    dispatch(setTotalStat(total));
  } catch (error) {
    dispatch(setError(error));
  }
};

export const loadUpdatedStat = (): AppThunk => async (dispatch) => {
  try {
    const pubID = await UserService.get()?.id;
    const result = await StatisticService.updateStats(pubID);

    const normalizedResult = {};

    Object.keys(result).forEach((key) => {
      // @ts-ignore
      normalizedResult[key.split("-").pop()] = result[key];
    });

    dispatch(setUpdatedStat(normalizedResult));
  } catch (error) {
    dispatch(setError(error));
  }
};

export const loadActiveSubscribers = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setActiveSubscribers({
      ...initialState.activeSubscribers,
      isFetching: true,
    }))
    const countActiveUsers = await ActiveUsersService.getActiveUsers();
    dispatch(setActiveSubscribers({
      ...initialState.activeSubscribers,
      data: countActiveUsers.count,
    }))
  } catch (error: Error | unknown) {
    dispatch(setActiveSubscribers({
      ...initialState.activeSubscribers,
      error: error,
    }))
  }
}

export default statisticSlice.reducer;
